package fr.manyverse.app.presentation.viewmodel.gamemaster

import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.launchAsyncMapped
import fr.manyverse.app.presentation.viewmodel.launchAsyncWithCallback
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val INITIAL_EVENTS_FETCH = 20
private const val INITIAL_EVENTS_DISPLAY = 10
private const val MORE_EVENTS_LIMIT = 50

/**
 * Owns the GameMaster activity feed (history of recent table events) and its pagination.
 * Extracted from GameMasterViewModel; behavior is intentionally identical.
 */
class GameMasterEventsDelegate(
    private val scope: CoroutineScope,
    private val tableId: String,
    private val getTableEventsUseCase: GetTableEventsUseCase,
    private val excludedFromHistory: Set<EventType>
) {
    private val _eventsState = MutableStateFlow<AsyncUiState<List<GameEvent>>>(AsyncUiState.Idle)
    val eventsState: StateFlow<AsyncUiState<List<GameEvent>>> = _eventsState.asStateFlow()

    private val _events = MutableStateFlow<List<GameEvent>>(emptyList())
    val events: StateFlow<List<GameEvent>> = _events.asStateFlow()

    private val _loadMoreEventsState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val loadMoreEventsState: StateFlow<AsyncUiState<Unit>> = _loadMoreEventsState.asStateFlow()

    private val _hasMoreEvents = MutableStateFlow(false)
    val hasMoreEvents: StateFlow<Boolean> = _hasMoreEvents.asStateFlow()

    private var dbEventsCount = 0

    fun loadEvents() {
        dbEventsCount = 0
        scope.launchAsyncWithCallback(
            _eventsState,
            { getTableEventsUseCase(tableId, INITIAL_EVENTS_FETCH, 0) }
        ) { eventList ->
            dbEventsCount = eventList.size
            _hasMoreEvents.value = eventList.size >= INITIAL_EVENTS_FETCH
            _events.value = eventList
                .filter { it.eventType !in excludedFromHistory }
                .take(INITIAL_EVENTS_DISPLAY)
        }
    }

    /**
     * Ajoute au fil les événements reçus pendant une coupure du temps réel, qui ne sont pas rejoués,
     * sans repasser par l'état de chargement. En cas d'échec (déjà remonté par le mapper), le fil
     * affiché reste en place.
     */
    fun refreshEvents() {
        scope.launch {
            getTableEventsUseCase(tableId, INITIAL_EVENTS_FETCH, 0).onSuccess { latest ->
                _events.update { current ->
                    (latest.filter { it.eventType !in excludedFromHistory } + current)
                        .distinctBy { it.id }
                        .sortedByDescending { it.createdAt }
                }
            }
        }
    }

    fun loadMoreEvents() {
        scope.launchAsyncMapped(
            _loadMoreEventsState,
            { getTableEventsUseCase(tableId, MORE_EVENTS_LIMIT, dbEventsCount) }
        ) { eventList ->
            dbEventsCount += eventList.size
            _hasMoreEvents.value = eventList.size >= MORE_EVENTS_LIMIT
            _events.update { current ->
                (current + eventList.filter { it.eventType !in excludedFromHistory })
                    .distinctBy { it.id }
                    .sortedByDescending { it.createdAt }
            }
        }
    }

    /** Prepends a Realtime event to the feed unless its type is excluded from history. */
    fun onRealtimeEvent(event: GameEvent) {
        if (event.eventType !in excludedFromHistory) {
            _events.update { listOf(event) + it }
        }
    }
}
